import Loading from '@/components/Loading';
import SignInConfirmModal from '@/components/SignInConfirmModal';
import { Dialog, DialogClose, DialogPortal, DialogPrimitiveContent, DialogTitle } from '@/components/ui/dialog';
import ViverseButton from '@/components/ViverseButton';
import ViverseTextarea from '@/components/ViverseTextarea';
import { useAuthContext } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { createTipPurchaseAction } from '@/server/lib/market';
import { getTipAmountList } from '@/services/market';
import { DialogOverlay } from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { env } from '@/lib/runtime-env';
import { ComponentProps, MouseEventHandler, ReactNode, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import useSWR from 'swr';
import { useMediaQuery } from 'usehooks-ts';
const THANKS_MESSAGE_MAX_LENGTH = 500;
const CURRENCY_SYMBOLS = {
  USD: 'USD$',
  TWD: 'NT$',
  JPY: '¥'
};
type TipFormData = {
  tipAmount: number | null;
  message?: string;
};
type TipModalProps = ComponentProps<typeof Dialog> & {
  recipientName?: string;
  dialogTrigger?: ReactNode;
  containerClassName?: string;
  creatorId: string;
  worldId?: string;
  onClose?: MouseEventHandler<HTMLButtonElement>;
};
export default function TipModal({
  recipientName = 'Creator',
  dialogTrigger,
  containerClassName,
  creatorId,
  worldId,
  onClose,
  ...props
}: TipModalProps) {
  const t = useTranslations();
  const locale = useLocale();
  const {
    isSignedIn
  } = useAuthContext();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [isTipModalOpen, setIsTipModalOpen] = useState(false);
  const isLandscapeMode = useMediaQuery('(orientation: landscape) and (max-height: 684px)');
  const {
    control,
    handleSubmit,
    reset
  } = useForm<TipFormData>({
    mode: 'onChange',
    defaultValues: {
      tipAmount: null,
      message: ''
    }
  });
  const {
    data: tipAmountData
  } = useSWR(`getTipAmountList-${locale}`, async () => {
    const res = await getTipAmountList();
    return res?.results || null;
  });
  const tipAmount = useWatch({
    control,
    name: 'tipAmount'
  });
  const handleFormSubmit = handleSubmit(async data => {
    setIsRedirecting(true);
    try {
      const {
        cached_cart
      } = await createTipPurchaseAction(creatorId, data.tipAmount!, worldId, data.message);
      if (cached_cart) {
        window.location.href = `${env('NEXT_PUBLIC_MARKET_SERVER')}/checkout?cached_cart=${cached_cart}&return_url=${window.location.href}#tip-success`;
      }
    } finally {
      setIsRedirecting(false);
    }
  });
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      reset();
      setIsTipModalOpen(false);
    }
  };
  const handleTriggerClick = () => {
    if (!isSignedIn) {
      setShowSignInModal(true);
    } else {
      setIsTipModalOpen(true);
    }
  };
  return <>
      <Dialog {...props} open={isTipModalOpen} onOpenChange={handleOpenChange}>
        {dialogTrigger && <div onClick={handleTriggerClick} className="block size-max">
            {dialogTrigger}
          </div>}
        <DialogPortal>
          <DialogOverlay className="" />
          <DialogPrimitiveContent tabIndex={-1} className={cn('bg-vive-surface-normal/40 466:w-[488px] 466:h-fit dialog-scrollbar 466:rounded-2xl 466:mt-0 466:px-10 466:py-8 fixed top-1/2 left-1/2 z-overlay mt-[72px] flex h-full w-full -translate-x-1/2 -translate-y-1/2 flex-col overflow-auto px-8 py-25 pt-6 shadow-xl backdrop-blur-[50px]', isLandscapeMode && '466:rounded-none 466:h-full 466:w-full 466:px-8 466:pt-6 466:pb-25 466:mt-[76px]', containerClassName)} onPointerDownOutside={e => e.preventDefault()} onInteractOutside={e_0 => e_0.preventDefault()}>
            <>
              {isRedirecting && <Loading />}
              <form onSubmit={handleFormSubmit} className="flex flex-col gap-6">
                <div className={'flex w-full items-center justify-between'}>
                  <div className="size-7" />
                  <DialogTitle className={'subheader2-omni-styles text-center'}>
                    {`${t('tip-modal.beginning-of-title')} ${recipientName}`}
                  </DialogTitle>
                  <DialogClose onClick={onClose} aria-label="close">
                    <X size={24} />
                  </DialogClose>
                </div>
                <p className="body1-omni-styles text-vive-primary-20 text-center">
                  {t('tip-modal.description')}
                </p>
                <div className="flex w-full flex-col gap-4 align-middle">
                  <p className="subheader2-omni-styles">{t('tip-modal.choose-amount')}</p>
                  <Controller name="tipAmount" control={control} rules={{
                  required: true
                }} render={({
                  field
                }) => <div className={cn('grid w-full grid-cols-3 gap-4 landscape:w-fit landscape:grid-cols-6')}>
                        {tipAmountData?.tip_amount_list?.map(amount => <label key={amount} className={cn('bg-vive-primary-70 hover:bg-vive-primary-60 active:bg-primary 446:w-30 flex h-12 w-full cursor-pointer items-center justify-center rounded-full px-4 py-1 transition-all', isLandscapeMode && '466:w-[98px]', field.value === amount && 'bg-primary hover:bg-primary')}>
                            <input type="radio" name="tipAmount" value={amount} checked={field.value === amount} onChange={() => field.onChange(amount)} className="sr-only" />
                            <span className="subheader2-omni-styles text-vive-primary-20">
                              {`${CURRENCY_SYMBOLS[tipAmountData.currency]}${amount}`}
                            </span>
                          </label>)}
                      </div>} />
                </div>
                <div className="flex w-full flex-col gap-4 align-middle">
                  <div>
                    <span className="text-vive-primary-20 subheader4-omni-styles mr-1">
                      {t('tip-modal.add-message')}
                    </span>
                    <span className="body2-omni-styles text-vive-primary-30">{`(${t('tip-modal.optional')})`}</span>
                    <Controller name="message" control={control} render={({
                    field: field_0
                  }) => <ViverseTextarea {...field_0} containerClassName="mt-2 flex-col items-end border-none px-2 pb-2 pt-1 bg-vive-primary-70/50" className="dialog-scrollbar 466:max-h-[80px] overflow-auto bg-transparent" maxLength={THANKS_MESSAGE_MAX_LENGTH} placeholder={t('tip-modal.message-placeholder')} />} />
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <ViverseButton type="submit" className={cn('buttonM-omni-styles 466:buttonL-omni-styles disabled:bg-vive-primary-80/30 466:h-14 h-11.5 w-full px-6 py-3 disabled:text-white/40', isLandscapeMode && '466:h-11.5 466:max-w-100 466:buttonM-omni-styles')} variant="default" size="custom" disabled={!tipAmount}>
                      {!!tipAmount ? `${t('tip-modal.send-tip')} — $${tipAmount}` : t('tip-modal.send-tip')}
                    </ViverseButton>
                    <div className="text-vive-primary-20 body2-omni-styles flex justify-center align-middle">
                      <p>{t('tip-modal.creator-earnings')}</p>
                    </div>
                  </div>
                </div>
              </form>
            </>
          </DialogPrimitiveContent>
        </DialogPortal>
      </Dialog>
      <SignInConfirmModal open={showSignInModal} onClose={() => setShowSignInModal(false)} content={t('tip-modal.sign-in-required')} />
    </>;
}